import getConfig from 'next/config'

import { Types as AuthTypes } from '@grandvisionhq/auth'
import type { Context as CartContext } from '@grandvisionhq/carts-v2'
import type { CheckoutSettings, Routes, ShippingMethodsLogos } from '@grandvisionhq/checkout-v2'

import { CheckoutAddressForm } from '../components/checkout/forms/Address'
import ContactDetailsForm from '../components/checkout/forms/ContactDetails'
import PickupNameForm from '../components/checkout/forms/PickupName'
import AddressSummary from '../components/checkout/summaries/Address'
import ContactDetailsSummary from '../components/checkout/summaries/ContactDetails'
import PickupNameSummary from '../components/checkout/summaries/PickupName'

interface SettingsInput {
  cart: CartContext.CartContextModel['cart']
  shippingMethodsLogos?: ShippingMethodsLogos
}

const { publicRuntimeConfig } = getConfig()
const { orderTimeOut } = publicRuntimeConfig.checkout

// Configuration for forms required to fill in Checkout shipping step
export const stepShippingFormsConfig = {
  contactDetails: ContactDetailsForm,
  billingAddress: CheckoutAddressForm,
  deliveryAddress: CheckoutAddressForm,
  pickupName: PickupNameForm,
}

// Configuration for summaries on Checkout Overview step
export const stepOverviewSummariesConfig = {
  contactDetails: ContactDetailsSummary,
  billingAddress: AddressSummary,
  deliveryAddress: AddressSummary,
  pickupName: PickupNameSummary,
}

export const basePath = '/checkout'
export const routes: Routes = {
  shipping: {
    label: 'Leverans',
    path: `${basePath}/leverans`,
  },
  overview: {
    label: 'Översikt',
    path: `${basePath}/oversikt`,
  },
  payment: {
    label: 'Betalning',
    path: `${basePath}/betalning`,
  },
  callback: {
    label: 'Callback',
    path: `${basePath}/callback`,
  },
  completed: {
    label: 'Genomförd',
    path: `${basePath}/genomford`,
  },
}

export const getCheckoutConfig = ({
  cart,
  shippingMethodsLogos,
}: SettingsInput): CheckoutSettings => {
  const paymentMethodLogos = {
    swish: {
      url: 'https://cdn.grandvision.io/binaries/_ht_1555402648638/content/gallery/se-synoptik/logos/payment-options/swish.svg',
    },
  }

  const optIns: AuthTypes.OptInItem[] = [
    {
      titleKey: 'account.optIn.marketingEmails',
      value: 'marketingEmailsOptIn',
    },
  ]

  let shippingDetails: CheckoutSettings['shippingDetails'] | undefined

  if (cart && (cart.billingAddress || cart.shippingAddress || cart.customerDetails)) {
    shippingDetails = {
      ...(cart.billingAddress ? { billingAddress: cart.billingAddress } : {}),
      ...(cart.shippingAddress ? { deliveryAddress: cart.shippingAddress } : {}),
      ...(cart.customerDetails ? { customer: cart.customerDetails } : {}),
    }
  }

  return {
    account: {
      implicitAccountCreation: false,
      createAccountRequired: false,
    },
    consent: {
      implicitTermsAndConditions: true,
      optIns,
    },
    shipping: {
      explicitShippingMethod: true,
    },
    shippingDetails: shippingDetails ? shippingDetails : {},
    shippingMethodsLogos: shippingMethodsLogos ? shippingMethodsLogos : {},
    storeFinder: {},
    ...(orderTimeOut ? { orderCreationTimeOutLimit: orderTimeOut } : {}),
    features: {
      isCompanyEnabled: false,
      isServiceStoreEnabled: false,
    },
    paymentMethodLogos,
    login: {
      disableSignup: true,
      returnPath: routes.shipping.path,
    },
    routes,
    payments: {
      applePay: {
        merchantName: 'Synoptik',
      },
    },
    logoVariant: 'main',
  }
}
